'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { entryService } from '@/lib/supabaseService';
import { CLIMBS, type ClimbEntry } from '@/lib/climbData';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';

// ─── Strava validation types ──────────────────────────────────────────────────

interface StravaValidationResult {
  valid: boolean;
  activityId: string | null;
  activityName: string | null;
  activityDate: string | null;
  athleteName: string | null;
  segmentFound: boolean;
  segmentName: string | null;
  segmentEffortTime: number | null;
  issues: string[];
  error: string | null;
}

type ValidationState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; result: StravaValidationResult }
  | { status: 'error'; message: string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatSeconds(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// ─── Inline validation panel ──────────────────────────────────────────────────

interface ValidationPanelProps {
  entry: ClimbEntry;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

function ValidationPanel({ entry, onClose, onApprove, onReject }: ValidationPanelProps) {
  const [state, setState] = useState<ValidationState>({ status: 'idle' });

  const climb = CLIMBS.find((c) => c.id === entry.climbId);

  const runValidation = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const res = await fetch('/api/validate-strava-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stravaActivityUrl: entry.stravaActivityUrl,
          stravaSegmentId: climb?.stravaSegmentId ?? '',
          climbName: climb?.name ?? entry.climbId,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result: StravaValidationResult = await res.json();
      setState({ status: 'done', result });
    } catch (err: any) {
      setState({ status: 'error', message: err?.message ?? 'Errore di rete' });
    }
  }, [entry, climb]);

  useEffect(() => {
    runValidation();
  }, [runValidation]);

  const isUrlMissing =
    !entry.stravaActivityUrl ||
    entry.stravaActivityUrl === '#' ||
    entry.stravaActivityUrl.trim() === '';

  return (
    <div className="border-b border-border/50 bg-card/80 px-6 py-4 fade-in">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-orange-400/20 flex items-center justify-center flex-shrink-0">
            <Icon name="LinkIcon" size={12} className="text-orange-400" />
          </div>
          <p className="text-xs font-semibold text-foreground">Verifica URL Strava</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="XMarkIcon" size={14} />
        </button>
      </div>

      {/* URL display */}
      <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-muted/40 rounded-lg">
        <Icon name="LinkIcon" size={12} className="text-muted-foreground flex-shrink-0" />
        {isUrlMissing ? (
          <span className="text-xs text-red-400 font-medium">Nessun URL Strava fornito</span>
        ) : (
          <a
            href={entry.stravaActivityUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-orange-400 hover:text-orange-300 truncate transition-colors"
          >
            {entry.stravaActivityUrl}
          </a>
        )}
      </div>

      {/* Validation result */}
      {state.status === 'loading' && (
        <div className="flex items-center gap-2 py-3">
          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="text-xs text-muted-foreground">Verifica in corso…</span>
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-400/10 border border-red-400/20 rounded-lg mb-3">
          <Icon name="ExclamationCircleIcon" size={14} className="text-red-400 flex-shrink-0" />
          <span className="text-xs text-red-400">Errore di connessione: {state.message}</span>
          <button
            onClick={runValidation}
            className="ml-auto text-xs text-red-400 hover:text-red-300 underline transition-colors"
          >
            Riprova
          </button>
        </div>
      )}

      {state.status === 'done' && (
        <div className="space-y-2 mb-3">
          {/* Overall status badge */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            state.result.valid && state.result.segmentFound
              ? 'bg-green-400/10 border-green-400/20'
              : state.result.valid
              ? 'bg-yellow-400/10 border-yellow-400/20' :'bg-red-400/10 border-red-400/20'
          }`}>
            <Icon
              name={
                state.result.valid && state.result.segmentFound
                  ? 'CheckCircleIcon'
                  : state.result.valid
                  ? 'ExclamationTriangleIcon' :'XCircleIcon'
              }
              size={14}
              className={
                state.result.valid && state.result.segmentFound
                  ? 'text-green-400 flex-shrink-0'
                  : state.result.valid
                  ? 'text-yellow-400 flex-shrink-0' :'text-red-400 flex-shrink-0'
              }
            />
            <span className={`text-xs font-semibold ${
              state.result.valid && state.result.segmentFound
                ? 'text-green-400'
                : state.result.valid
                ? 'text-yellow-400' :'text-red-400'
            }`}>
              {state.result.valid && state.result.segmentFound
                ? 'URL valido — segmento corrispondente trovato'
                : state.result.valid
                ? 'URL raggiungibile — segmento non verificato'
                : 'Validazione fallita'}
            </span>
          </div>

          {/* Activity details */}
          {(state.result.activityName || state.result.activityDate || state.result.athleteName) && (
            <div className="grid grid-cols-3 gap-2">
              {state.result.activityName && (
                <div className="px-2 py-1.5 bg-muted/30 rounded-lg">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Attività</p>
                  <p className="text-xs font-medium text-foreground truncate">{state.result.activityName}</p>
                </div>
              )}
              {state.result.activityDate && (
                <div className="px-2 py-1.5 bg-muted/30 rounded-lg">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Data attività</p>
                  <p className="text-xs font-mono-nums font-medium text-foreground">{state.result.activityDate}</p>
                </div>
              )}
              {state.result.athleteName && (
                <div className="px-2 py-1.5 bg-muted/30 rounded-lg">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Atleta</p>
                  <p className="text-xs font-medium text-foreground truncate">{state.result.athleteName}</p>
                </div>
              )}
            </div>
          )}

          {/* Segment match details */}
          {state.result.segmentFound && state.result.segmentName && (
            <div className="flex items-center gap-3 px-3 py-2 bg-green-400/5 border border-green-400/20 rounded-lg">
              <Icon name="MapPinIcon" size={12} className="text-green-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground">Segmento trovato</p>
                <p className="text-xs font-semibold text-green-400 truncate">{state.result.segmentName}</p>
              </div>
              {state.result.segmentEffortTime !== null && (
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-muted-foreground">Tempo segmento</p>
                  <p className="text-xs font-mono-nums font-bold text-green-400">
                    {formatSeconds(state.result.segmentEffortTime)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Issues list */}
          {state.result.issues.length > 0 && (
            <div className="space-y-1">
              {state.result.issues.map((issue, i) => (
                <div
                  key={`issue-${i}`}
                  className="flex items-start gap-2 px-3 py-1.5 bg-amber-400/5 border border-amber-400/20 rounded-lg"
                >
                  <Icon name="ExclamationTriangleIcon" size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300/90">{issue}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        {state.status === 'done' && (
          <button
            onClick={runValidation}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="ArrowPathIcon" size={12} />
            Ricontrolla
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={onReject}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-400/10 text-red-400 hover:bg-red-400/20 border border-red-400/30 transition-colors"
        >
          <Icon name="XMarkIcon" size={12} />
          Rifiuta
        </button>
        <button
          onClick={onApprove}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            state.status === 'done' && state.result.valid && state.result.segmentFound
              ? 'bg-green-400/10 text-green-400 hover:bg-green-400/20 border-green-400/30'
              : state.status === 'done'&& !state.result.valid ?'bg-yellow-400/10 text-yellow-400 hover:bg-yellow-400/20 border-yellow-400/30' :'bg-green-400/10 text-green-400 hover:bg-green-400/20 border-green-400/30'
          }`}
        >
          <Icon name="CheckIcon" size={12} />
          {state.status === 'done' && !state.result.valid
            ? 'Approva comunque' :'Approva'}
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminEntriesTab() {
  const [entries, setEntries] = useState<ClimbEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'suspicious'>('pending');
  const [filterClimb, setFilterClimb] = useState<string>('all');
  const [rejectTarget, setRejectTarget] = useState<ClimbEntry | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedReasons, setExpandedReasons] = useState<string | null>(null);
  const [validatingEntry, setValidatingEntry] = useState<string | null>(null);

  useEffect(() => {
    entryService.getAllForAdmin().then(setEntries).catch(() => setEntries([])).finally(() => setLoading(false));
  }, []);

  const filtered = entries.filter((e) => {
    const matchClimb = filterClimb === 'all' || e.climbId === filterClimb;
    if (filterStatus === 'suspicious') return e.suspicious && matchClimb;
    const matchStatus = filterStatus === 'all' || e.status === filterStatus;
    return matchStatus && matchClimb;
  });

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleApprove = async (entryId: string) => {
    try {
      await entryService.updateStatus(entryId, 'approved');
      setEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, status: 'approved' as const } : e));
      setValidatingEntry(null);
      toast.success('Tempo approvato e pubblicato in classifica');
    } catch {
      toast.error('Errore durante l\'approvazione');
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await entryService.updateStatus(rejectTarget.id, 'rejected');
      setEntries((prev) => prev.map((e) => e.id === rejectTarget.id ? { ...e, status: 'rejected' as const } : e));
      setRejectTarget(null);
      setRejectReason('');
      setValidatingEntry(null);
      toast.error('Tempo rifiutato');
    } catch {
      toast.error('Errore durante il rifiuto');
    }
  };

  const handleBulkApprove = async () => {
    try {
      await Promise.all(Array.from(selectedIds).map((id) => entryService.updateStatus(id, 'approved')));
      setEntries((prev) => prev.map((e) => selectedIds.has(e.id) ? { ...e, status: 'approved' as const } : e));
      toast.success(`${selectedIds.size} tempi approvati`);
      setSelectedIds(new Set());
    } catch {
      toast.error('Errore durante l\'approvazione multipla');
    }
  };

  const handleClearSuspicion = async (entryId: string) => {
    try {
      await entryService.flagSuspicious(entryId, false, []);
      setEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, suspicious: false, suspiciousReasons: [] } : e));
      toast.success('Flag sospetto rimosso');
    } catch {
      toast.error('Errore durante la rimozione del flag');
    }
  };

  const climbName = (id: string) => CLIMBS.find((c) => c.id === id)?.name ?? id;
  const pendingCount = entries.filter((e) => e.status === 'pending').length;
  const suspiciousCount = entries.filter((e) => e.suspicious).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'In attesa', count: entries.filter((e) => e.status === 'pending').length, color: 'text-yellow-400', bg: 'bg-yellow-400/10', icon: 'ClockIcon' },
          { label: 'Approvati', count: entries.filter((e) => e.status === 'approved').length, color: 'text-green-400', bg: 'bg-green-400/10', icon: 'CheckCircleIcon' },
          { label: 'Rifiutati', count: entries.filter((e) => e.status === 'rejected').length, color: 'text-red-400', bg: 'bg-red-400/10', icon: 'XCircleIcon' },
          { label: 'Sospetti', count: suspiciousCount, color: 'text-amber-400', bg: 'bg-amber-400/10', icon: 'ExclamationTriangleIcon' },
        ].map((s) => (
          <div key={`entry-stat-${s.label}`} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className={`${s.bg} rounded-lg p-2`}>
              <Icon name={s.icon as 'ClockIcon'} size={18} className={s.color} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className={`text-xl font-bold font-mono-nums ${s.color}`}>{s.count}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Suspicious alert banner */}
      {suspiciousCount > 0 && filterStatus !== 'suspicious' && (
        <div
          className="flex items-center gap-3 mb-4 px-4 py-3 bg-amber-400/10 border border-amber-400/30 rounded-xl cursor-pointer hover:bg-amber-400/15 transition-colors"
          onClick={() => setFilterStatus('suspicious')}
        >
          <Icon name="ExclamationTriangleIcon" size={18} className="text-amber-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-400">
              {suspiciousCount} {suspiciousCount === 1 ? 'attività sospetta rilevata' : 'attività sospette rilevate'} — revisione manuale richiesta
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Clicca per visualizzare le attività sospette</p>
          </div>
          <Icon name="ChevronRightIcon" size={16} className="text-amber-400 flex-shrink-0" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {([
            { key: 'all', label: 'Tutti' },
            { key: 'pending', label: `In attesa${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
            { key: 'approved', label: 'Approvati' },
            { key: 'rejected', label: 'Rifiutati' },
            { key: 'suspicious', label: `⚠ Sospetti${suspiciousCount > 0 ? ` (${suspiciousCount})` : ''}` },
          ] as const).map((s) => (
            <button
              key={`entry-filter-${s.key}`}
              onClick={() => setFilterStatus(s.key)}
              className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all duration-150 ${
                filterStatus === s.key
                  ? s.key === 'suspicious' ? 'bg-amber-400 text-black border-amber-400' : 'bg-primary text-primary-foreground border-primary'
                  : s.key === 'suspicious'&& suspiciousCount > 0 ?'border-amber-400/50 text-amber-400 hover:bg-amber-400/10' :'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <select value={filterClimb} onChange={(e) => setFilterClimb(e.target.value)} className="input-field px-3 py-2 text-sm rounded-lg">
          <option value="all">Tutte le salite</option>
          {CLIMBS.map((c) => (
            <option key={`climb-opt-${c.id}`} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Bulk bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-primary/10 border border-primary/30 rounded-xl fade-in">
          <span className="text-sm font-semibold text-primary">{selectedIds.size} selezionati</span>
          <button onClick={handleBulkApprove} className="btn-primary px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
            <Icon name="CheckCircleIcon" size={14} />
            Approva tutti
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="btn-ghost px-3 py-1.5 rounded-lg text-xs font-semibold">Deseleziona</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={selectedIds.size === filtered.length && filtered.length > 0} onChange={() => { if (selectedIds.size === filtered.length) setSelectedIds(new Set()); else setSelectedIds(new Set(filtered.map((e) => e.id))); }} className="w-4 h-4 rounded border-border bg-input accent-primary" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Ciclista</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Salita</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Tempo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">W/kg</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Data</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Fonte</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center">
                    <Icon name={filterStatus === 'suspicious' ? 'ExclamationTriangleIcon' : 'ClockIcon'} size={36} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-semibold text-foreground mb-1">Nessun tempo trovato</p>
                    <p className="text-xs text-muted-foreground">
                      {filterStatus === 'pending' ? 'Nessun tempo in attesa di verifica. Ottimo!' :
                       filterStatus === 'suspicious' ? 'Nessuna attività sospetta rilevata. Tutto in ordine!' : 'Nessun tempo corrisponde ai filtri selezionati.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((entry) => (
                  <React.Fragment key={`admin-entry-${entry.id}`}>
                    <tr className={`border-b border-border/50 row-hover ${entry.suspicious ? 'bg-amber-400/5' : ''} ${validatingEntry === entry.id ? 'bg-orange-400/5' : ''}`}>
                      <td className="px-4 py-3.5">
                        <input type="checkbox" checked={selectedIds.has(entry.id)} onChange={() => toggleSelect(entry.id)} className="w-4 h-4 rounded border-border bg-input accent-primary" />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                            {entry.userAvatar}
                          </div>
                          <span className="font-semibold text-foreground text-sm whitespace-nowrap">{entry.userName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5"><span className="text-sm text-muted-foreground whitespace-nowrap">{climbName(entry.climbId)}</span></td>
                      <td className="px-4 py-3.5"><span className="font-mono-nums font-bold text-foreground">{entry.timeFormatted}</span></td>
                      <td className="px-4 py-3.5"><span className="font-mono-nums text-sm text-foreground">{entry.wattPerKg.toFixed(1)}</span></td>
                      <td className="px-4 py-3.5"><span className="text-xs text-muted-foreground font-mono-nums">{entry.date}</span></td>
                      <td className="px-4 py-3.5">
                        {entry.importedFromStrava ? (
                          <span className="flex items-center gap-1 text-xs text-orange-400 font-semibold whitespace-nowrap">
                            <span className="w-2 h-2 rounded-full bg-orange-400" />Strava
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground font-semibold">
                            <span className="w-2 h-2 rounded-full bg-muted-foreground" />Manuale
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex flex-col gap-1">
                          <Badge variant={entry.status === 'approved' ? 'approved' : entry.status === 'pending' ? 'pending' : 'rejected'}>
                            {entry.status === 'approved' ? 'Approvato' : entry.status === 'pending' ? 'In attesa' : 'Rifiutato'}
                          </Badge>
                          {entry.suspicious && (
                            <button
                              onClick={() => setExpandedReasons(expandedReasons === entry.id ? null : entry.id)}
                              className="flex items-center gap-1 text-xs text-amber-400 font-semibold hover:text-amber-300 transition-colors"
                              title="Mostra motivi sospetto"
                            >
                              <Icon name="ExclamationTriangleIcon" size={12} />
                              Sospetto
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1">
                          <a href={entry.stravaActivityUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg bg-orange-400/10 text-orange-400 hover:bg-orange-400/20 transition-colors" title="Vedi attività Strava">
                            <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                          </a>
                          {entry.status === 'pending' && (
                            <>
                              {/* Verify URL button — opens inline validation panel */}
                              <button
                                onClick={() => setValidatingEntry(validatingEntry === entry.id ? null : entry.id)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  validatingEntry === entry.id
                                    ? 'bg-orange-400/20 text-orange-300' :'bg-orange-400/10 text-orange-400 hover:bg-orange-400/20'
                                }`}
                                title="Verifica URL Strava prima di approvare"
                              >
                                <Icon name="ShieldCheckIcon" size={14} />
                              </button>
                              <button
                                onClick={() => handleApprove(entry.id)}
                                className="p-1.5 rounded-lg bg-green-400/10 text-green-400 hover:bg-green-400/20 transition-colors"
                                title="Approva tempo"
                              >
                                <Icon name="CheckIcon" size={14} />
                              </button>
                              <button
                                onClick={() => { setRejectTarget(entry); setRejectReason(''); }}
                                className="p-1.5 rounded-lg bg-red-400/10 text-red-400 hover:bg-red-400/20 transition-colors"
                                title="Rifiuta tempo"
                              >
                                <Icon name="XMarkIcon" size={14} />
                              </button>
                            </>
                          )}
                          {entry.status === 'approved' && (
                            <button onClick={() => { setRejectTarget(entry); setRejectReason(''); }} className="p-1.5 rounded-lg bg-red-400/10 text-red-400 hover:bg-red-400/20 transition-colors" title="Revoca approvazione">
                              <Icon name="XMarkIcon" size={14} />
                            </button>
                          )}
                          {entry.suspicious && (
                            <button
                              onClick={() => handleClearSuspicion(entry.id)}
                              className="p-1.5 rounded-lg bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-colors"
                              title="Rimuovi flag sospetto"
                            >
                              <Icon name="ShieldCheckIcon" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Strava URL validation panel */}
                    {validatingEntry === entry.id && (
                      <tr className="border-b border-orange-400/20">
                        <td colSpan={9} className="p-0">
                          <ValidationPanel
                            entry={entry}
                            onClose={() => setValidatingEntry(null)}
                            onApprove={() => handleApprove(entry.id)}
                            onReject={() => { setRejectTarget(entry); setRejectReason(''); setValidatingEntry(null); }}
                          />
                        </td>
                      </tr>
                    )}

                    {/* Expanded suspicious reasons row */}
                    {entry.suspicious && expandedReasons === entry.id && (
                      <tr className="border-b border-amber-400/20 bg-amber-400/5">
                        <td colSpan={9} className="px-6 py-3">
                          <div className="flex items-start gap-2">
                            <Icon name="ExclamationTriangleIcon" size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-semibold text-amber-400 mb-1.5">Motivi del flag sospetto:</p>
                              <ul className="space-y-1">
                                {entry.suspiciousReasons.map((reason, i) => (
                                  <li key={`reason-${entry.id}-${i}`} className="text-xs text-amber-300/80 flex items-start gap-1.5">
                                    <span className="text-amber-400 mt-0.5">•</span>
                                    {reason}
                                  </li>
                                ))}
                              </ul>
                              <div className="flex gap-2 mt-3">
                                <button
                                  onClick={() => handleApprove(entry.id)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-400/10 text-green-400 hover:bg-green-400/20 border border-green-400/30 transition-colors flex items-center gap-1"
                                >
                                  <Icon name="CheckIcon" size={12} />
                                  Approva comunque
                                </button>
                                <button
                                  onClick={() => { setRejectTarget(entry); setRejectReason(''); setExpandedReasons(null); }}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-400/10 text-red-400 hover:bg-red-400/20 border border-red-400/30 transition-colors flex items-center gap-1"
                                >
                                  <Icon name="XMarkIcon" size={12} />
                                  Rifiuta
                                </button>
                                <button
                                  onClick={() => handleClearSuspicion(entry.id)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 border border-amber-400/30 transition-colors flex items-center gap-1"
                                >
                                  <Icon name="ShieldCheckIcon" size={12} />
                                  Rimuovi flag
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject modal */}
      <Modal open={!!rejectTarget} onClose={() => setRejectTarget(null)} title="Rifiuta tempo">
        <p className="text-sm text-muted-foreground mb-3">
          Stai per rifiutare il tempo di <span className="font-semibold text-foreground">{rejectTarget?.userName}</span> su <span className="font-semibold text-foreground">{climbName(rejectTarget?.climbId ?? '')}</span>.
        </p>
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Motivo del rifiuto (opzionale)..."
          className="input-field w-full px-4 py-2.5 text-sm rounded-lg resize-none h-20 mb-4"
        />
        <div className="flex gap-3 justify-end">
          <button onClick={() => setRejectTarget(null)} className="btn-ghost px-4 py-2 rounded-lg text-sm font-semibold">Annulla</button>
          <button onClick={handleReject} className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-400/10 text-red-400 hover:bg-red-400/20 border border-red-400/30 transition-colors">Rifiuta</button>
        </div>
      </Modal>
    </div>
  );
}